<template>
    <div v-if="lang" class="estates-list-heading">
        <div class="estates-count">
            <h2>
            <img alt="pin-agency" :src="'/img/icons/' + $root.network + '/pin-agency.svg'" class="geo " width="13" height="18"/>
            <slot v-if="ssr" name="estates-count" />
            <template v-else>
                <strong>{{ count }}</strong> {{ lang.labels[section] }}
                <template v-if="hidden_count > 0">
                    {{ lang.labels.of_which }} <strong>{{ hidden_count }} </strong>{{ hidden_count == 1 ? lang.labels.single_hidden : lang.labels.multi_hidden }}
                </template>
            </template>
            </h2>
        </div>

        <div v-if="fallback_filters" class="estates-list-fallback">
            <p class="img">
                <img alt="fallback" :src="'/img/icons/' + $root.network + '/list-fallback.svg'">
            </p>
            <p>{{ lang.labels.fallback | capitalize }}</p>
            <span
                class="badge"
                v-for="(filter, index) in fallback_filters"
                :key="index"
            >
                {{ filter.title | capitalize }}
            </span>
        </div>

        <div v-if="!ssr && count <= 0" class="estates-list-noresults">
            <img src="/img/icons/no-results.svg" class="no-results" alt="no results"/>
            <h4 class="font-weight-bold">{{ lang.heading.no_estates_found }}</h4>
            <div v-if="section == 'estate'">
                {{ lang.heading.no_estates_found_alert }}<br>
                <button
                    type="button"
                    class="btn btn-network mail-alert"
                    @click.prevent="openSaveSearchModal"
                >
                    <masked-image src="/img/icons/email.svg"></masked-image>
                    <span>{{ lang.heading.save_search }}</span>
                </button>
            </div>
        </div>

        <div v-if="['estate', 'construction'].includes(section)" class="estates-actions">
            <div class="estates-filters-toggle" @click="toggleFilters">
                <img src="/img/icons/filters.svg" :alt="lang.heading.labels.filter" width="16" height="16">
                {{ lang.heading.labels.filter }}
                <div
                    v-if="primaryFiltersLength"
                    class="badge"
                    :class="'badge-' + $root.network"
                >
                    {{ primaryFiltersLength }}
                </div>
                <img :src="'/img/icons/' + $root.network + '/search.svg'" alt="Cerca" width="16" height="16">
            </div>

            <div
                v-if="sort_visible && section == 'estate'"
                class="estates-sort"
                @click.stop="$refs.sort_filters.activate()">
                <img src="/img/icons/sort.svg" class="ordina" :alt="lang.heading.labels.sort" width="16" height="12">

                <my-multiselect
                    ref="sort_filters"
                    v-model="search.sort"
                    :options="config.sort_filters"
                    label="key" clearable
                    :placeholder="lang.heading.labels.sort"
                    track-by="value"
                    @input="updatedSort"
                    :custom-label="customLabel"
                />
            </div>
        </div>

        <keep-alive>
            <modal
                v-if="this.$root.is_mobile"
                name="estates_search_filters"
                ref="estates_search_filters"
                :title="lang.heading.modal_title"
                extra-class="full-screen-modal estates-search-filters-modal"
            >
                <template slot="body">
                    <estates-search-filters
                        :config="config"
                        :config_map="config_map"
                        :search-data="search"
                        :geo_data="geo_data"
                        :available-filters="available_filters"
                        :section="section"
                        :types_list="types_list"
                    />
                </template>
            </modal>
        </keep-alive>
    </div>
</template>

<script>
import { updateUriParam } from '../../helpers/urls'
import { countObject, filterObject, filterObjectByKey } from '../../helpers/objects'

import Modal from '../Modal'
import SearchFilters from './SearchFilters'

export default {
    components: {
        'estates-search-filters': SearchFilters,
        'modal': Modal,
    },

    props: {
        config: {
            type: Object,
            required: true,
        },

        config_map: {
            type: Object,
            required: true,
        },

        fallback: {
            type: Object
        },

        geo_data: {
            type: Object,
            required: true,
        },

        search: {
            type: Object,
            required: true,
        },

        section: {
            type: String,
            required: true,
        },

        sort_enable: {
            type: Boolean,
            required: false,
        },

        types_list: {
            type: Array,
        },
    },

    data() {
        return {
            available_filters: {},
            ssr: true,
            sort_visible: this.sort_enable,
            count: 0,
            fallback_filters: this.fallback,
            hidden_count: 0,
            primary_filters: null,
            sort_filters: [
                {
                    key: 'recent',
                    value: 'Aggiornamento|DESC'
                },
                {
                    key: 'price_updated',
                    value: 'immobileRibassato|DESC'
                },
                {
                    key: 'price_desc',
                    value: 'Prezzo|DESC'
                },
                {
                    key: 'price_asc',
                    value: 'Prezzo|ASC'
                },
                {
                    key: 'rooms_desc',
                    value: 'Camere|DESC'
                },
                {
                    key: 'rooms_asc',
                    value: 'Camere|ASC'
                },
                {
                    key: 'sq_desc',
                    value: 'MQ|DESC'
                },
                {
                    key: 'sq_asc',
                    value: 'MQ|ASC'
                }
            ]
        }
    },

    computed: {
        lang() {
            return this.$root.lang.estates_list ? this.$root.lang.estates_list : null
        },

        primaryFiltersLength() {
            return countObject(this.primary_filters)
        }
    },

    methods: {
        customLabel ({ key }) {
            return this.lang.sort_filters[key]
        },

        openSaveSearchModal() {
            window.events.$emit('showModal', { name: 'save_search' })
        },

        toggleFilters() {
            window.events.$emit('showModal', { name: 'estates_search_filters' })
        },

        updatedSort(value) {
            let newUrl = updateUriParam(null, 'sort', value)
            newUrl = newUrl.replace(new RegExp(/\/pag-(\d+)/), '')
            window.location = newUrl
        },

        primaryFilters() {
            let primary_filters = [
                'air_conditioning',
                'balcony',
                'bathrooms',
                'concierge',
                'furnitured',
                'garage',
                'garden',
                'heating',
                'lift',
                'max_rooms',
                'max_price',
                'max_surface',
                'min_ce',
                'min_price',
                'min_rooms',
                'min_surface',
                'property_type',
                'status',
                'subsubtype',
                // 'type',
                'virtual_tour',
            ]

            let filters = filterObjectByKey(this.search, key => {
                return primary_filters.indexOf(key) > -1
            })

            filters = filterObject(filters, item => {
                return item
            })

            this.primary_filters = filters
        },
    },

    mounted() {
        window.events.$on('list.heading.count', event => {
            this.ssr = false
            this.count = event
            this.sort_visible = (event) ? true : false
        })

        window.events.$on('list.heading.hidden_count', event => {
            this.hidden_count = event
        })

        window.events.$on('list.fallback', event => {
            this.fallback_filters = event
        }),

        window.events.$on('primary-filters-update', event => {
            this.primaryFilters()
        })

        this.primaryFilters()
    },
}
</script>